import { Vector3 } from '@babylonjs/core';
import { Clock } from '../core/Clock';
import { InputRouter } from '../core/input';
import { Audio } from '../core/Audio';
import { Persist } from '../core/Persist';
import { SceneManager, type TableMeshes } from '../render/SceneManager';
import { PhysicsManager, FlipperController } from '../physics';
import { DebugOverlay } from './DebugOverlay';
import {
  type GameMode,
  type RunState,
  type InputState,
  type ITableDef,
  DEFAULT_RUN_STATE,
  toVector3,
} from '../core/types';
import { TABLE_01 } from '../tables/table01';

/**
 * Top-level game application.
 * Owns the game loop, all subsystems, and lifecycle management.
 */
export class GameApp {
  // Core systems
  readonly clock: Clock;
  readonly input: InputRouter;
  readonly audio: Audio;
  readonly persist: Persist;

  // Rendering
  readonly sceneManager: SceneManager;
  readonly debugOverlay: DebugOverlay;

  // Physics
  readonly physics: PhysicsManager;
  readonly flippers: FlipperController;

  // Game state
  private runState: RunState = { ...DEFAULT_RUN_STATE };
  private currentTable: ITableDef = TABLE_01;
  private tableMeshes: TableMeshes | null = null;

  // Input tracking for edge detection
  private prevLeftFlipper = false;
  private prevRightFlipper = false;

  // Cached input state for current frame
  private lastInput: InputState | null = null;

  // Loop control
  private rafId: number | null = null;
  private disposed = false;
  private physicsReady = false;

  // Callbacks
  onReady: (() => void) | null = null;
  onWin: ((time: number, medal: string) => void) | null = null;
  onFell: (() => void) | null = null;

  constructor(canvas: HTMLCanvasElement) {
    // Initialize core systems (30 = max dt clamped at 1/30s)
    this.clock = new Clock(30);
    this.input = new InputRouter();
    this.audio = new Audio();
    this.persist = new Persist();

    // Apply saved settings
    const settings = this.persist.settings;
    this.input.applySettings({
      invertTiltX: settings.invertTiltX,
      invertTiltY: settings.invertTiltY,
      tiltSensitivity: settings.tiltSensitivity,
    });
    this.input.setCalibration(this.persist.calibrationOffset);
    this.audio.setSfxVolume(settings.sfxVolume);
    this.audio.setMusicVolume(settings.musicVolume);

    // Initialize rendering
    this.sceneManager = new SceneManager(canvas);
    this.debugOverlay = new DebugOverlay(
      document.getElementById('debug-overlay')!
    );
    // The diagnostics HUD is a developer aid, never part of the public game.
    this.debugOverlay.visible = import.meta.env.DEV && settings.showDebug;

    // Initialize physics
    this.physics = new PhysicsManager(this.sceneManager.scene);
    this.flippers = new FlipperController(this.sceneManager.scene);

    // Attach input
    this.input.attach(canvas);

    // Async initialization
    this.initAsync();
  }

  /** Async initialization (physics WASM loading) */
  private async initAsync(): Promise<void> {
    try {
      // Load physics
      await this.physics.init();
      this.physicsReady = true;

      // Subscribe to target collisions
      this.physics.onTargetHitObservable.add((targetId) => {
        this.handleTargetHit(targetId);
      });

      // Now set up table with physics
      this.loadTable(this.currentTable);

      // Check motion permission
      this.checkMotionPermission();

      // Notify ready
      this.onReady?.();

      console.log('Physics initialized');
    } catch (e) {
      console.error('Failed to initialize physics:', e);
    }
  }

  /** Load a table */
  private loadTable(table: ITableDef): void {
    // Clean up old physics bodies before loading new table
    if (this.physicsReady) {
      this.physics.clearBodies();
      this.flippers.dispose();
    }

    this.currentTable = table;

    // Create meshes
    this.tableMeshes = this.sceneManager.createTable(table);
    this.sceneManager.focusOnTable(table);

    // Set up physics bodies
    if (this.physicsReady && this.tableMeshes) {
      // Board
      this.physics.setupBoard(this.tableMeshes.board);

      // Walls
      for (const wall of this.tableMeshes.walls) {
        this.physics.setupWall(wall);
      }

      // Ball
      this.physics.setupBall(this.tableMeshes.ball);

      // Flippers
      for (const flipperDef of table.flippers) {
        this.flippers.createFlipper(flipperDef, true);
      }

      // Targets get physics with their ID for collision events
      for (const [id, mesh] of this.tableMeshes.targets) {
        this.physics.setupTarget(mesh, id);
      }

      // Finish gate (blocks ball until all targets hit)
      this.physics.setupFinishGate(this.tableMeshes.finish);
    }

    this.resetRun();
  }

  /** Reset the current run */
  resetRun(): void {
    const wasPermissionGate = this.runState.mode === 'permission-gate';

    this.runState = {
      ...DEFAULT_RUN_STATE,
      mode: wasPermissionGate ? 'permission-gate' : (this.physicsReady ? 'playing' : 'loading'),
      totalTargets: this.currentTable.targets.length,
      bestTime: this.persist.getBestTime(this.currentTable.id),
      targetsHit: new Set(),
    };

    // Reset ball position
    if (this.physicsReady) {
      this.physics.resetBall(toVector3(this.currentTable.spawnPosition));
    }

    // Reset target visuals
    for (const target of this.currentTable.targets) {
      this.sceneManager.setTargetHit(target.id, false);
    }
    this.sceneManager.setGateOpen(false);
    if (this.physicsReady) {
      this.physics.setFinishGateOpen(false);
    }

    this.clock.reset();
  }

  /** Check and potentially request motion permission */
  private async checkMotionPermission(): Promise<void> {
    // Desktop (no touch): use keyboard tilt, no permission needed
    if (!this.input.isTouchDevice) {
      if (this.physicsReady) {
        this.runState.mode = 'playing';
      }
      return;
    }

    // Motion sensor unavailable: use drag tilt fallback
    if (this.input.tiltInput.permission === 'unavailable') {
      if (this.physicsReady) {
        this.runState.mode = 'playing';
      }
      return;
    }

    // Already granted
    if (this.input.tiltInput.permission === 'granted') {
      if (this.physicsReady) {
        this.runState.mode = 'playing';
      }
      return;
    }

    // Check if gesture-based permission is required (iOS Safari web)
    if (!this.input.needsMotionGesture()) {
      await this.input.requestMotionPermission();
      if (this.physicsReady) {
        this.runState.mode = 'playing';
      }
      return;
    }

    // iOS Safari: check if we previously granted
    if (this.persist.motionPermissionGranted) {
      const result = await this.input.requestMotionPermission();
      if (result === 'granted') {
        this.runState.mode = 'playing';
        return;
      }
    }

    // Need to show permission gate (iOS Safari requires user gesture)
    this.runState.mode = 'permission-gate';
  }

  /** Request motion permission (must be called from user gesture) */
  async requestMotionPermission(): Promise<void> {
    const result = await this.input.requestMotionPermission();
    await this.audio.resume(); // Also resume audio on user gesture

    // Save permission state so we don't ask again
    if (result === 'granted') {
      this.persist.setMotionPermissionGranted(true);
    }

    if (this.physicsReady) {
      this.runState.mode = 'playing';
    }

    if (result !== 'granted') {
      console.log('Motion denied, using drag/keyboard fallback');
    }
  }

  /** Calibrate tilt to current orientation */
  calibrate(): void {
    const offset = this.input.calibrate();
    this.persist.setCalibrationOffset(offset);
  }

  /** Get current input state (returns cached state from last poll) */
  getInputState(): InputState {
    return this.lastInput ?? this.input.state;
  }

  /** Get current run state */
  getRunState(): RunState {
    return this.runState;
  }

  /** Get current game mode */
  get mode(): GameMode {
    return this.runState.mode;
  }

  /** Start the game loop */
  start(): void {
    if (this.rafId !== null) return;

    const loop = (timestamp: number) => {
      if (this.disposed) return;

      // Get clamped dt for this frame
      const dt = this.clock.tick(timestamp);

      // Update (simulation) - single call per frame to match Havok physics stepping
      if (dt > 0) {
        this.update(dt);
      }

      // Render
      this.render();

      // Schedule next frame
      this.rafId = requestAnimationFrame(loop);
    };

    this.rafId = requestAnimationFrame(loop);
  }

  /** Stop the game loop */
  stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /** Update (simulation) - called once per frame with clamped dt */
  private update(dt: number): void {
    // Poll input once per frame
    const input = this.input.poll(dt);
    this.lastInput = input;

    // Update flippers (input is already in game space from InputRouter)
    this.flippers.setFlipperState('left', input.leftFlipper);
    this.flippers.setFlipperState('right', input.rightFlipper);
    this.flippers.update(dt);

    // Flipper audio on edge
    if (input.leftFlipper && !this.prevLeftFlipper) {
      this.audio.playFlipperUp();
    } else if (!input.leftFlipper && this.prevLeftFlipper) {
      this.audio.playFlipperDown();
    }
    if (input.rightFlipper && !this.prevRightFlipper) {
      this.audio.playFlipperUp();
    } else if (!input.rightFlipper && this.prevRightFlipper) {
      this.audio.playFlipperDown();
    }
    this.prevLeftFlipper = input.leftFlipper;
    this.prevRightFlipper = input.rightFlipper;

    if (this.runState.mode !== 'playing' || !this.physicsReady) return;

    // Update timer using raw (unclamped) dt for accurate real-time tracking
    this.runState.elapsedTime += this.clock.rawDt;

    // Apply tilt force to ball
    this.physics.setTiltInput(input.tilt, dt);

    // Check holes
    this.checkHoles();

    // Check out of bounds
    if (this.physics.isBallOutOfBounds(this.currentTable)) {
      this.handleOutOfBounds();
    }

    // Check finish
    if (this.runState.gateOpen) {
      this.checkFinish();
    }
  }

  /** Handle target hit event (from physics) */
  private handleTargetHit(targetId: string): void {
    // Skip already hit
    if (this.runState.targetsHit.has(targetId)) return;

    // Hit!
    this.runState.targetsHit.add(targetId);
    this.sceneManager.setTargetHit(targetId, true);
    this.audio.playTargetHit();

    // Check if all targets hit
    if (this.runState.targetsHit.size >= this.runState.totalTargets) {
      this.runState.gateOpen = true;
      this.sceneManager.setGateOpen(true);
      this.physics.setFinishGateOpen(true);
      this.audio.playGateOpen();
    }
  }

  /** Check if ball fell in a hole */
  private checkHoles(): void {
    for (const holeDef of this.currentTable.holes) {
      if (this.physics.checkHoleFall(toVector3(holeDef.position), holeDef.radius)) {
        this.handleOutOfBounds();
        return;
      }
    }
  }

  /** Handle ball going out of bounds */
  private handleOutOfBounds(): void {
    this.audio.playFall();
    this.physics.stopBall();
    this.runState.mode = 'fell';
    this.onFell?.();
  }

  /** Check if ball reached finish */
  private checkFinish(): void {
    const { x, z } = this.currentTable.finishPosition;
    if (this.physics.checkTargetHit(new Vector3(x, 0, z), 0.8)) {
      this.handleFinish();
    }
  }

  /** Handle completing the run */
  private handleFinish(): void {
    this.runState.mode = 'finished';
    this.audio.playFinish();

    const time = this.runState.elapsedTime;
    const medals = this.currentTable.medalTimes;

    // Determine medal
    if (time <= medals.gold) {
      this.runState.medal = 'gold';
    } else if (time <= medals.silver) {
      this.runState.medal = 'silver';
    } else if (time <= medals.bronze) {
      this.runState.medal = 'bronze';
    }

    // Save best time
    const isNewBest = this.persist.setBestTime(this.currentTable.id, time);
    if (isNewBest) {
      this.runState.bestTime = time;
    }

    console.log(`Finished in ${time.toFixed(2)}s - ${this.runState.medal} medal${isNewBest ? ' (NEW BEST!)' : ''}`);

    // Notify UI
    this.onWin?.(time, this.runState.medal);
  }

  /** Render frame */
  private render(): void {
    const input = this.lastInput ?? this.input.state;

    // Update debug overlay
    const vel = this.physics.getBallVelocity();
    const ballSpeed = vel ? vel.length() : 0;
    const angVel = this.physics.getBallAngularVelocity();
    const angularSpeed = angVel ? angVel.length() : 0;

    this.debugOverlay.update({
      fps: this.clock.fps,
      dt: this.clock.renderDt,
      activeMeshes: this.sceneManager.activeMeshCount,
      mode: this.runState.mode,
      elapsed: this.runState.elapsedTime,
      tilt: input.tilt,
      rawTilt: input.rawTilt,
      ballSpeed,
      planarSpeed: this.physics.getPlanarSpeed(),
      angularSpeed,
      slipRatio: this.physics.getSlipRatio(),
    });

    // Render scene
    this.sceneManager.render();
  }

  /** Clean up all resources */
  dispose(): void {
    this.disposed = true;
    this.stop();

    this.input.dispose();
    this.audio.dispose();
    this.flippers.dispose();
    this.physics.dispose();
    this.sceneManager.dispose();
  }
}
